// ============================================================================
// psyResultsSummary.ts — ДАННЫЕ сводки результатов кабинета психолога.
//
// Сводка собирается ТОЛЬКО существующими RPC (метаданные + чтение таблиц):
//   • пациенты ядра «ГидраМедик» (medic_patients);
//   • назначения методик (psy_assignments);
//   • методики банка (типы опросов + шаблоны);
//   • протоколы — физические таблицы типов опросов (последние 200 на методику).
//
// Никаких новых серверных механизмов, proto и миграций. Права — SSoT сервера:
// отказ в чтении протоколов возвращается флагом resultsDenied, а не пустым
// списком (честное ограничение доступа). Ошибки отдельных методик — в warnings,
// остальная сводка остаётся доступной.
//
// Статусы строки (детерминированно, по имеющимся данным):
//   attention  — прохождение ЕСТЬ, но порог НЕ пройден (passed = false);
//   completed  — прохождение есть (порог пройден или не определён);
//   overdue    — назначение активно, но ссылка истекла либо оно старше 7 дней;
//   pending    — назначение активно и ещё ожидает прохождения.
//
// Завершённые назначения (done/cancelled) отдельными строками не дублируются:
// результат по методике уже показан строкой прохождения.
// ============================================================================

import type { TableDataRow } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { readThrough } from '@/lib/offlineCache';
import {
  isNotFoundError,
  isPermissionDeniedError,
  logSwallowed,
} from '@/lib/errors';
import {
  ASSIGNMENTS,
  loadMethods,
  type PsyAssignment,
  type PsyMethod,
} from '@/lib/psyWorkspace';

const PATIENTS_TABLE = 'medic_patients';
const PATIENTS_PAGE_SIZE = 1000;
const ASSIGNMENTS_PAGE_SIZE = 1000;
/** «Последние прохождения»: свежие сверху, ограничение на методику. */
const RESULTS_PER_METHOD = 200;
/** Сколько строк отдаём панели (важные статусы сохраняются всегда). */
const MAX_ROWS = 250;
/** Назначение без ссылки старше этого срока считается просроченным. */
const ASSIGNMENT_OVERDUE_DAYS = 7;

export type PsySummaryStatus = 'attention' | 'completed' | 'overdue' | 'pending';
export type PsySummaryFilter = 'all' | PsySummaryStatus;

export interface PsySummaryRow {
  /** Устойчивый ключ строки (результат либо назначение). */
  key: string;
  kind: 'result' | 'assignment';
  patientId: string;
  patientName: string;
  methodCode: string;
  methodName: string;
  status: PsySummaryStatus;
  /** Дата прохождения (результат) или дата назначения (ожидание). */
  date: string;
  score: string;
  /** null — порог не определён (нет порога/балла), а не «не пройден». */
  passed: boolean | null;
  conclusion: string;
  assignmentStatus: string;
  linkExpiresAt: string;
}

export interface PsySummaryCounts {
  all: number;
  attention: number;
  completed: number;
  overdue: number;
  pending: number;
}

export interface PsySummaryData {
  rows: PsySummaryRow[];
  counts: PsySummaryCounts;
  /** Хотя бы одна методика закрыта правами роли — показать явно. */
  resultsDenied: boolean;
  /** Ошибки отдельных методик (сводка при этом остаётся доступной). */
  warnings: string[];
  patientsTotal: number;
  methodsTotal: number;
  /** Полный набор больше MAX_ROWS — показаны последние + все важные строки. */
  truncated: boolean;
  rowCount: number;
}

type FlatRow = Record<string, string> & { id: string };

interface RawResult {
  id: string;
  methodCode: string;
  methodName: string;
  subjectId: string;
  createdAt: string;
  score: string;
  passedRaw: string;
  conclusion: string;
}

function slug(): string {
  return useCenterStore.getState().baseSlug || 'local';
}

function cellsOf(row: TableDataRow): Record<string, string> {
  const out: Record<string, string> = {};
  for (const value of row.values ?? []) {
    out[String(value.column)] = value.value === null || value.value === undefined
      ? ''
      : String(value.value);
  }
  return out;
}

function rowsOf(rows: TableDataRow[]): FlatRow[] {
  return rows.map((row) => ({ id: String(row.id || ''), ...cellsOf(row) }));
}

function passedState(value: string): boolean | null {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === '') return null;
  if (normalized === 'true' || normalized === '1' || normalized === 'да') return true;
  if (normalized === 'false' || normalized === '0' || normalized === 'нет') return false;
  return null;
}

function timeOf(value: string): number {
  const raw = String(value || '').trim();
  if (!raw) return 0;
  const parsed = Date.parse(raw);
  if (!Number.isNaN(parsed)) return parsed;
  // Go/SQLite: "2026-08-07 02:29:03 +0000 UTC" — Date.parse не понимает,
  // но порядок значений сохраняется и при разборе ведущей даты/времени.
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}):(\d{2}))?/.exec(raw);
  if (!match) return 0;
  return Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4] || 0),
    Number(match[5] || 0),
    Number(match[6] || 0),
  );
}

function patientLabel(row: FlatRow): string {
  return String(row.name || row.fio || '').trim() || 'Пациент';
}

async function loadPatientNames(): Promise<Map<string, string>> {
  const result = await readThrough(
    `psy:summary:patients:${slug()}`,
    async () => {
      const response = await getSDK().metadata.listTableData({
        schema: 'public',
        table: PATIENTS_TABLE,
        pageSize: PATIENTS_PAGE_SIZE,
        page: 0,
      });
      return rowsOf(response.rows)
        .filter((row) => row.id)
        .map((row) => [row.id, patientLabel(row)] as [string, string]);
    },
  );
  return new Map(result.data);
}

async function loadAllAssignments(): Promise<PsyAssignment[]> {
  const result = await readThrough(
    `psy:summary:assignments:${slug()}`,
    async () => {
      const response = await getSDK().metadata.listTableData({
        schema: 'public',
        table: ASSIGNMENTS,
        pageSize: ASSIGNMENTS_PAGE_SIZE,
        page: 0,
      });
      return rowsOf(response.rows).map((row) => ({
        id: row.id,
        patientId: row.patient_id || '',
        methodCode: row.method_code || '',
        status: row.status || 'assigned',
        assignedDate: row.assigned_date || row.created_at || '',
        linkToken: row.link_token || '',
        linkExpiresAt: row.link_expires_at || '',
      }));
    },
  );
  return result.data;
}

interface MethodResults {
  items: RawResult[];
  denied: boolean;
  warnings: string[];
}

async function loadResultsForMethods(methods: PsyMethod[]): Promise<MethodResults> {
  if (methods.length === 0) return { items: [], denied: false, warnings: [] };
  const sdk = getSDK();
  const outcomes = await Promise.all(methods.map(async (method) => {
    try {
      const response = await sdk.metadata.listTableData({
        schema: 'public',
        table: method.code,
        pageSize: RESULTS_PER_METHOD,
        page: 0,
        orderBy: 'created_at',
        orderDesc: true,
      });
      const items: RawResult[] = rowsOf(response.rows).map((row) => ({
        id: row.id,
        methodCode: method.code,
        methodName: method.synonym,
        subjectId: row.subject_id || '',
        createdAt: row.result_date || row.created_at || '',
        score: row.score || '',
        passedRaw: row.passed || '',
        conclusion: row.conclusion || '',
      }));
      return { method, items, denied: false, error: null as unknown };
    } catch (e) {
      if (isPermissionDeniedError(e)) {
        return { method, items: [] as RawResult[], denied: true, error: null as unknown };
      }
      if (!isNotFoundError(e)) logSwallowed(e, `psyResultsSummary: протоколы ${method.code}`);
      return { method, items: [] as RawResult[], denied: false, error: e };
    }
  }));

  const items: RawResult[] = [];
  const warnings: string[] = [];
  let denied = false;
  for (const outcome of outcomes) {
    items.push(...outcome.items);
    if (outcome.denied) denied = true;
    if (outcome.error && !isNotFoundError(outcome.error)) warnings.push(outcome.method.synonym);
  }
  return { items, denied, warnings };
}

async function loadResults(methods: PsyMethod[]): Promise<MethodResults> {
  const methodKey = methods.map((method) => method.code).sort().join('|');
  const result = await readThrough(
    `psy:summary:results:${slug()}:${methodKey}`,
    () => loadResultsForMethods(methods),
  );
  return result.data;
}

function assignmentOverdue(assignment: PsyAssignment): boolean {
  const now = Date.now();
  if (assignment.linkExpiresAt) {
    const expires = timeOf(assignment.linkExpiresAt);
    if (expires > 0 && expires <= now) return true;
  }
  const assigned = timeOf(assignment.assignedDate);
  if (assigned > 0 && now - assigned > ASSIGNMENT_OVERDUE_DAYS * 24 * 60 * 60 * 1000) return true;
  return false;
}

function isActiveAssignment(assignment: PsyAssignment): boolean {
  return String(assignment.status || '').trim().toLowerCase() === 'assigned';
}

function byDateDesc(a: PsySummaryRow, b: PsySummaryRow): number {
  const diff = timeOf(b.date) - timeOf(a.date);
  if (diff !== 0) return diff;
  return a.key.localeCompare(b.key);
}

/**
 * Сводка результатов: последние прохождения + ожидающие/просроченные назначения.
 * Бросает ошибку только если недоступны ключевые данные (пациенты/назначения/
 * методики) — остальное деградирует явными предупреждениями.
 */
export async function loadPsyResultsSummary(): Promise<PsySummaryData> {
  const methods = await loadMethods();
  const [patientNames, assignments] = await Promise.all([
    loadPatientNames(),
    loadAllAssignments(),
  ]);
  const results = await loadResults(methods);

  const methodByCode = new Map(methods.map((method) => [method.code, method]));

  const resultRows: PsySummaryRow[] = results.items.map((item) => {
    const passed = passedState(item.passedRaw);
    const hasPatient = !!item.subjectId;
    return {
      key: `result:${item.methodCode}:${item.id}`,
      kind: 'result',
      patientId: item.subjectId,
      patientName: hasPatient
        ? patientNames.get(item.subjectId) || 'Пациент не найден в картотеке'
        : 'Пациент не указан',
      methodCode: item.methodCode,
      methodName: item.methodName,
      status: passed === false ? 'attention' : 'completed',
      date: item.createdAt,
      score: item.score,
      passed,
      conclusion: item.conclusion,
      assignmentStatus: '',
      linkExpiresAt: '',
    };
  });

  // Последний результат по паре «пациент + методика» — чтобы не показывать
  // назначение второй строкой, если оно уже выполнено. Повторное назначение
  // ПОСЛЕ прошлого результата остаётся ожидающим (свежая дата назначения).
  const latestResultAt = new Map<string, number>();
  for (const row of resultRows) {
    if (!row.patientId || !row.methodCode) continue;
    const key = `${row.patientId}:${row.methodCode}`;
    const at = timeOf(row.date);
    if (at >= (latestResultAt.get(key) ?? 0)) latestResultAt.set(key, at);
  }

  const assignmentRows: PsySummaryRow[] = [];
  for (const assignment of assignments) {
    if (!isActiveAssignment(assignment)) continue;
    if (assignment.patientId && assignment.methodCode) {
      const resultAt = latestResultAt.get(`${assignment.patientId}:${assignment.methodCode}`);
      const assignedAt = timeOf(assignment.assignedDate);
      if (resultAt !== undefined && (assignedAt === 0 || resultAt >= assignedAt)) {
        // Результат уже есть после этого назначения — не дублируем строку.
        continue;
      }
    }
    const method = methodByCode.get(assignment.methodCode) || null;
    assignmentRows.push({
      key: `assignment:${assignment.id}`,
      kind: 'assignment',
      patientId: assignment.patientId,
      patientName: assignment.patientId
        ? patientNames.get(assignment.patientId) || 'Пациент не найден в картотеке'
        : 'Пациент не указан',
      methodCode: assignment.methodCode,
      methodName: method?.synonym || assignment.methodCode || 'Методика',
      status: assignmentOverdue(assignment) ? 'overdue' : 'pending',
      date: assignment.assignedDate,
      score: '',
      passed: null,
      conclusion: '',
      assignmentStatus: assignment.status,
      linkExpiresAt: assignment.linkExpiresAt,
    });
  }

  const allRows = [...resultRows, ...assignmentRows].sort(byDateDesc);
  const important = allRows.filter((row) => row.status === 'attention' || row.status === 'overdue');
  const rest = allRows.filter((row) => row.status !== 'attention' && row.status !== 'overdue');
  const keepRest = Math.max(0, MAX_ROWS - important.length);
  const rows = [...important, ...rest.slice(0, keepRest)].sort(byDateDesc);

  const counts: PsySummaryCounts = {
    all: allRows.length,
    attention: 0,
    completed: 0,
    overdue: 0,
    pending: 0,
  };
  for (const row of allRows) counts[row.status] += 1;

  return {
    rows,
    counts,
    resultsDenied: results.denied,
    warnings: results.warnings,
    patientsTotal: patientNames.size,
    methodsTotal: methods.length,
    truncated: allRows.length > rows.length,
    rowCount: allRows.length,
  };
}
